import * as React from 'react';
import { type ButtonSize, type ButtonVariant } from '@wayste/shadcn/components/button';
import { cn } from '@wayste/shadcn/lib/utils';

// ─── Context ──────────────────────────────────────────────────────────────────

interface ButtonGroupContextValue {
    size?: ButtonSize;
    variant?: ButtonVariant;
    orientation: 'horizontal' | 'vertical';
}

const ButtonGroupContext = React.createContext<ButtonGroupContextValue>({
    orientation: 'horizontal',
});

export function useButtonGroup() {
    return React.useContext(ButtonGroupContext);
}

// ─── Types ────────────────────────────────────────────────────────────────────

export interface ButtonGroupProps extends React.ComponentProps<'div'> {
    /** Shared variant applied to all child Buttons unless they override it. */
    variant?: ButtonVariant;
    /** Shared size applied to all child Buttons unless they override it. */
    size?: ButtonSize;
    /** Layout direction. Default: horizontal. */
    orientation?: 'horizontal' | 'vertical';
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * ButtonGroup
 *
 * Wraps multiple Button components into a visually connected unit.
 * Removes the gap between buttons and applies connected border styles by targeting
 * direct `[data-slot=button]` children.
 *
 * Pass `variant` and `size` to apply them to all child buttons uniformly.
 *
 * @example
 * ```tsx
 * <ButtonGroup>
 *   <Button>Left</Button>
 *   <Button>Center</Button>
 *   <Button>Right</Button>
 * </ButtonGroup>
 *
 * <ButtonGroup variant="secondary" size="sm">
 *   <Button>Day</Button>
 *   <Button>Week</Button>
 *   <Button>Month</Button>
 * </ButtonGroup>
 * ```
 */
function ButtonGroup({ className, variant, size, orientation = 'horizontal', children, ...props }: ButtonGroupProps) {
    return (
        <ButtonGroupContext.Provider value={{ variant, size, orientation }}>
            <div
                data-slot="button-group"
                data-orientation={orientation}
                className={cn(
                    // Remove individual button border-radius on interior edges
                    'inline-flex',
                    orientation === 'horizontal' && [
                        'flex-row',
                        // First child: round left side only
                        '[&>[data-slot=button]:first-child]:rounded-r-none',
                        // Last child: round right side only
                        '[&>[data-slot=button]:last-child]:rounded-l-none',
                        // Middle children: no rounding
                        '[&>[data-slot=button]:not(:first-child):not(:last-child)]:rounded-none',
                        // Collapse borders between buttons (prevent double border)
                        '[&>[data-slot=button]:not(:first-child)]:-ml-px',
                        // Raise z-index on focus/hover so border shows above adjacent button
                        '[&>[data-slot=button]:hover]:relative [&>[data-slot=button]:hover]:z-10',
                        '[&>[data-slot=button]:focus-visible]:relative [&>[data-slot=button]:focus-visible]:z-10',
                    ],
                    orientation === 'vertical' && [
                        'flex-col',
                        '[&>[data-slot=button]:first-child]:rounded-b-none',
                        '[&>[data-slot=button]:last-child]:rounded-t-none',
                        '[&>[data-slot=button]:not(:first-child):not(:last-child)]:rounded-none',
                        '[&>[data-slot=button]:not(:first-child)]:-mt-px',
                        '[&>[data-slot=button]:hover]:relative [&>[data-slot=button]:hover]:z-10',
                        '[&>[data-slot=button]:focus-visible]:relative [&>[data-slot=button]:focus-visible]:z-10',
                    ],
                    className,
                )}
                {...props}
            >
                {children}
            </div>
        </ButtonGroupContext.Provider>
    );
}

export { ButtonGroup };
